import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { Location } from '@shared/models/location';
import { LocationDetails, LocationDetailsUpdate } from '@shared/models/location-details';
import { environment } from '@environments/environment';

// Endpoint root
const APIURL = environment.apiUrl;

@Injectable({
  providedIn: 'root',
})
export class LocationService {
  private readonly http = inject(HttpClient);

  getList(): Observable<Location[]> {
    const storageKey = 'locations';
    const cached = sessionStorage.getItem(storageKey);
    if (cached) {
      return of(JSON.parse(cached) as Location[]);
    }
    return this.http.get<Location[]>(`${APIURL}/locations/`).pipe(
      map((data) => {
        sessionStorage.setItem(storageKey, JSON.stringify(data));
        return data;
      }),
    );
  }

  /** Address and catch-all mail. Not cached: it is only read by the edit dialog. */
  getDetails(id: number): Observable<LocationDetails> {
    return this.http.get<LocationDetails>(`${APIURL}/locations/${id}/details`);
  }

  updateDetails(id: number, payload: LocationDetailsUpdate): Observable<LocationDetails> {
    return this.http.put<LocationDetails>(`${APIURL}/locations/${id}/details`, payload);
  }

  getActiveLocations(isExternal = false): Observable<Location[]> {
    const storageKey = `active-locations-${isExternal}`;
    const params = `?isExternal=${isExternal}`;
    const cached = sessionStorage.getItem(storageKey);
    if (cached) {
      return of(JSON.parse(cached) as Location[]);
    }
    return this.http.get<Location[]>(`${APIURL}/locations/active-locations/${params}`).pipe(
      map((data) => {
        sessionStorage.setItem(storageKey, JSON.stringify(data));
        return data;
      }),
    );
  }

  get(id: number, expand: boolean): Observable<Location> {
    const params = expand ? '?expand=parent' : '';
    return this.http.get<Location>(`${APIURL}/locations/${id}/${params}`);
  }
}
